import { useEffect, useMemo, useState } from "react";
import { app } from "@microsoft/teams-js";
import { DEFAULT_LOCALE, SupportedLocale, resolveSupportedLocale } from "../i18n/locale";

type LocaleSource = "browser" | "teams";
type LocaleStatus = "loading" | "ready" | "fallback";

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error("Timed out while initializing Teams host context."));
    }, timeoutMs);

    promise.then(
      (value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      }
    );
  });
}

export function useHostLocale() {
  const [locale, setLocale] = useState<SupportedLocale>(DEFAULT_LOCALE);
  const [localeSource, setLocaleSource] = useState<LocaleSource>("browser");
  const [localeStatus, setLocaleStatus] = useState<LocaleStatus>("loading");

  useEffect(() => {
    let disposed = false;

    async function initializeTeamsLocale() {
      try {
        await withTimeout(app.initialize(), 1500);
        const context = await withTimeout(app.getContext(), 1500);

        if (disposed) return;

        setLocaleSource("teams");
        setLocale(resolveSupportedLocale(context.app.locale));
        setLocaleStatus("ready");
      } catch {
        if (!disposed) {
          setLocaleSource("browser");
          setLocale(DEFAULT_LOCALE);
          setLocaleStatus("fallback");
        }
      }
    }

    void initializeTeamsLocale();

    return () => {
      disposed = true;
    };
  }, []);

  return useMemo(
    () => ({
      isInTeams: localeSource === "teams",
      locale,
      localeStatus,
      localeSource,
    }),
    [locale, localeSource, localeStatus]
  );
}
